import {
  type ReactNode,
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useState,
} from "react";
import { LockKeyhole } from "lucide-react";
import { AuthProvider, useAuth } from "@/auth/AuthProvider";
import { AuthLoadingCard, AuthStage } from "@/auth/AuthStage";
import { ProtectedRoute } from "@/auth/ProtectedRoute";
import { type AuthClient } from "@/auth/api";
import {
  announceSSOStepUp,
  SSO_STEP_UP_FAILURES,
  SSO_STEP_UP_LANDING,
  stepUpMessageFrom,
} from "@/auth/ssoStepUp";
import { BrandedStateShell, StateCard } from "@/app/state";
import {
  useConsoleRedesignAttribute,
  useConsoleRedesignEnabled,
} from "@/hooks/useConsoleRedesign";
import { type EventSourceFactory } from "@/hooks/useNotificationStream";
import { type ConversationClient } from "@/lib/types";
import { type ShellNavigate } from "@/lib/types";
import {
  isCalendarAuthorizationReturn,
  relayCalendarAuthorizationReturn,
} from "@/v2/calendar/authorizationReturn";

const LoginPage = lazy(() => import("@/auth/LoginPage"));
const V2Shell = lazy(() => import("@/v2/V2Shell"));
const TOTPChallenge = lazy(() => import("@/auth/TOTPChallenge"));
const RecoveryChallenge = lazy(() => import("@/auth/RecoveryChallenge"));
const SetupPage = lazy(() => import("@/auth/SetupPage"));
const PasskeyOfferCard = lazy(() =>
  import("@/auth/PasskeyStageCards").then((module) => ({
    default: module.PasskeyOfferCard,
  })),
);
const RecoveryFinishCard = lazy(() =>
  import("@/auth/PasskeyStageCards").then((module) => ({
    default: module.RecoveryFinishCard,
  })),
);

interface AppRoutesProps {
  authClient?: AuthClient;
  createCredential?: (options: unknown) => Promise<unknown>;
  conversationClient?: ConversationClient;
  eventSourceFactory?: EventSourceFactory;
}

export function AppRoutes({
  authClient,
  createCredential,
  conversationClient,
  eventSourceFactory,
}: AppRoutesProps) {
  // The step-up window's landing page stands outside the auth provider: its
  // session restore would rotate the refresh token the opening tab shares and
  // sign that tab out the moment it had confirmed.
  if (window.location.pathname === SSO_STEP_UP_LANDING) {
    return <SSOStepUpLanding />;
  }
  if (isCalendarAuthorizationReturn()) return <CalendarConsentLanding />;
  return (
    <AuthProvider client={authClient} createCredential={createCredential}>
      <ConsoleRedesignBridge />
      <RouteSwitch
        conversationClient={conversationClient}
        eventSourceFactory={eventSourceFactory}
      />
    </AuthProvider>
  );
}

/** The workbench takes its accent from the tokens this flag sets (v2/wb.css),
 * so the flag outlives the console it was named for. */
function ConsoleRedesignBridge() {
  useConsoleRedesignAttribute(useConsoleRedesignEnabled());
  return null;
}

function RouteSwitch({
  conversationClient,
  eventSourceFactory,
}: Pick<AppRoutesProps, "conversationClient" | "eventSourceFactory">) {
  const [path, setPath] = useState(currentBrowserPath);
  const pathname = routePathname(path);
  // `replace` exists for canonicalization (`/` and bare section paths): the
  // honest URL lands without spending a history entry, so Back never returns
  // to a pre-canonical URL.
  const navigate = useCallback<ShellNavigate>((nextPath, options) => {
    if (options?.replace) window.history.replaceState(null, "", nextPath);
    else window.history.pushState(null, "", nextPath);
    setPath(currentBrowserPath());
  }, []);

  useEffect(() => {
    const syncPath = () => setPath(currentBrowserPath());
    window.addEventListener("popstate", syncPath);
    return () => window.removeEventListener("popstate", syncPath);
  }, []);

  const authScreen = authRouteScreen(pathname, navigate);
  if (authScreen) {
    // One stage around every auth route, so moving between them swaps only
    // the card: the globe keeps turning and focus is managed by the card.
    return (
      <AuthStage>
        <Suspense
          fallback={
            <AuthLoadingCard
              title={authScreen.loading}
              body="Getting the next step ready."
            />
          }
        >
          {authScreen.card}
        </Suspense>
      </AuthStage>
    );
  }

  return (
    <ProtectedRoute onRedirect={navigate}>
      <AuthenticatedGate
        path={path}
        navigate={navigate}
        conversationClient={conversationClient}
        eventSourceFactory={eventSourceFactory}
        onLoggedOut={() => navigate("/login")}
      />
    </ProtectedRoute>
  );
}

/** The card for an auth route, and what its lazy chunk says while loading. */
function authRouteScreen(
  pathname: string,
  navigate: ShellNavigate,
): { card: ReactNode; loading: string } | null {
  const returnTo = currentSafeReturnTo();
  const toSignIn = () => navigate(authPathWithReturnTo("/login", returnTo));
  switch (pathname) {
    case "/login":
      return {
        loading: "Loading sign in",
        card: (
          <LoginPage
            returnTo={returnTo}
            onTOTPRequired={() =>
              navigate(authPathWithReturnTo("/auth/totp", returnTo))
            }
            onAuthenticated={() => navigate(returnTo)}
            onPasswordSignedIn={() =>
              navigate(authPathWithReturnTo("/auth/passkey", returnTo), {
                replace: true,
              })
            }
          />
        ),
      };
    case "/auth/totp":
      return {
        loading: "Loading verification",
        card: (
          <TOTPChallenge
            onSignIn={toSignIn}
            onRecovery={() =>
              navigate(authPathWithReturnTo("/auth/recovery", returnTo))
            }
            onAuthenticated={() =>
              navigate(authPathWithReturnTo("/auth/passkey", returnTo), {
                replace: true,
              })
            }
          />
        ),
      };
    case "/auth/recovery":
      return {
        loading: "Loading verification",
        card: (
          <RecoveryChallenge
            onVerification={() =>
              navigate(authPathWithReturnTo("/auth/totp", returnTo))
            }
            onSignIn={toSignIn}
            onAuthenticated={() =>
              navigate(
                authPathWithReturnTo("/auth/recovery/finish", returnTo),
                {
                  replace: true,
                },
              )
            }
          />
        ),
      };
    case "/auth/passkey":
      return {
        loading: "Opening Starling",
        card: (
          <PasskeyOfferCard
            onDone={() => navigate(returnTo, { replace: true })}
            onRecovering={() =>
              navigate(
                authPathWithReturnTo("/auth/recovery/finish", returnTo),
                { replace: true },
              )
            }
          />
        ),
      };
    case "/auth/recovery/finish":
      return {
        loading: "Opening Starling",
        card: (
          <RecoveryFinishCard
            onDone={() => navigate(returnTo, { replace: true })}
          />
        ),
      };
    case "/auth/sso/callback":
      return {
        loading: "Completing sign in",
        card: <SSOCallbackCard navigate={navigate} />,
      };
    case "/setup":
      return {
        loading: "Loading account setup",
        card: (
          <SetupPage
            onComplete={() => navigate("/")}
            onSignIn={() => navigate("/login")}
          />
        ),
      };
    default:
      return null;
  }
}

function CalendarConsentLanding() {
  useEffect(() => relayCalendarAuthorizationReturn(), []);
  const status = new URLSearchParams(window.location.search).get("calendar");
  const connected = status === "connected";
  return (
    <BrandedStateShell
      variant="auth"
      routeLabel="Microsoft Calendar"
      mainLabel="Calendar sign-in"
    >
      <StateCard
        icon={LockKeyhole}
        tone={connected ? "success" : "error"}
        title={
          connected
            ? "Microsoft sign-in finished"
            : "Calendar was not connected"
        }
        body={
          connected
            ? "Close this window and return to Account settings in your original Starling window, where your calendar settings are."
            : status === "use_existing"
              ? "Close this window and return to Account settings in your original Starling window. Use Enable calendar or Reconnect calendar on the existing account."
              : "Close this window and return to Account settings in your original Starling window to try connecting again."
        }
      />
    </BrandedStateShell>
  );
}

/** Where a single sign-on step-up window comes back to. It tells the window
 * that is waiting how the sign-in went, then closes itself; when the browser
 * cut it off from that window, it says to go back by hand. */
function SSOStepUpLanding() {
  const [message] = useState(() => stepUpMessageFrom(window.location.search));

  useEffect(() => {
    announceSSOStepUp(message);
    const timer = window.setTimeout(() => window.close(), 150);
    return () => window.clearTimeout(timer);
  }, [message]);

  const confirmed = message.result === "ok";
  return (
    <BrandedStateShell
      variant="auth"
      routeLabel="Enterprise SSO"
      mainLabel={confirmed ? "Confirmed" : "Not confirmed"}
    >
      <StateCard
        icon={LockKeyhole}
        tone={confirmed ? "success" : "error"}
        title={confirmed ? "Confirmed" : "Not confirmed"}
        body={
          confirmed
            ? "You signed in again. You can close this window and go back to Starling."
            : `${SSO_STEP_UP_FAILURES[message.reason ?? "failed"]} You can close this window.`
        }
      />
    </BrandedStateShell>
  );
}

function SSOCallbackCard({ navigate }: { navigate: (path: string) => void }) {
  const { completeSSOExchange } = useAuth();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const exchangeCode = params.get("exchange_code") ?? "";
    const returnTo = safeReturnTo(params.get("return_to"));
    if (!exchangeCode) {
      navigate("/login");
      return;
    }
    let active = true;
    void completeSSOExchange(exchangeCode)
      .then(() => {
        if (active) {
          navigate(returnTo);
        }
      })
      .catch(() => {
        if (active) {
          navigate("/login");
        }
      });
    return () => {
      active = false;
    };
  }, [completeSSOExchange, navigate]);

  return (
    <AuthLoadingCard
      eyebrow="Enterprise SSO"
      title="Completing sign in"
      body="Checking your organization's sign-in before Starling opens."
    />
  );
}

function safeReturnTo(value: string | null) {
  if (
    !value ||
    !value.startsWith("/") ||
    value.startsWith("//") ||
    value === "/login" ||
    value.startsWith("/login?") ||
    value.startsWith("/login/") ||
    value === "/auth" ||
    value.startsWith("/auth/") ||
    value === "/setup" ||
    value.startsWith("/setup/")
  ) {
    return "/";
  }
  return value;
}

function currentSafeReturnTo() {
  const params = new URLSearchParams(window.location.search);
  return safeReturnTo(params.get("return_to"));
}

function authPathWithReturnTo(path: string, returnTo: string) {
  return returnTo === "/"
    ? path
    : `${path}?return_to=${encodeURIComponent(returnTo)}`;
}

function currentBrowserPath() {
  return `${window.location.pathname || "/"}${window.location.search || ""}`;
}

function routePathname(path: string) {
  return path.split(/[?#]/, 1)[0] || "/";
}

/**
 * Every authenticated path belongs to the workbench (docs/design/ui-shell);
 * its map, unknown paths and the console's old addresses included, lives in
 * `v2/shell/routes.ts`.
 */
function AuthenticatedGate({
  path,
  navigate,
  conversationClient,
  eventSourceFactory,
  onLoggedOut,
}: {
  path: string;
  navigate: ShellNavigate;
  conversationClient?: ConversationClient;
  eventSourceFactory?: EventSourceFactory;
  onLoggedOut: () => void;
}) {
  const auth = useAuth();
  if (auth.status !== "authenticated") {
    return null;
  }
  return (
    <Suspense
      fallback={
        <BrandedStateShell routeLabel="Starling" mainLabel="Loading workbench">
          <StateCard
            tone="loading"
            title="Loading workbench"
            body="Preparing the Starling workbench."
          />
        </BrandedStateShell>
      }
    >
      <V2Shell
        path={path}
        navigate={navigate}
        onLoggedOut={onLoggedOut}
        {...(conversationClient ? { conversationClient } : {})}
        {...(eventSourceFactory ? { eventSourceFactory } : {})}
      />
    </Suspense>
  );
}
